import { useState } from 'react';
import { X, Info, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { useActiveAnnouncements, type Announcement } from '@/hooks/queries/useAdminAnnouncements';
import { cn } from '@/lib/utils';

const typeConfig: Record<
  string,
  { icon: React.ComponentType<{ className?: string }>; bg: string; border: string; text: string }
> = {
  info: {
    icon: Info,
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
  },
  warning: {
    icon: AlertTriangle,
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
  },
  success: {
    icon: CheckCircle2,
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
  },
  error: {
    icon: XCircle,
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
  },
};

function SingleBanner({
  announcement,
  onDismiss,
}: {
  announcement: Announcement;
  onDismiss: () => void;
}) {
  const defaultConfig = {
    icon: Info,
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
  };
  const config = typeConfig[announcement.type] ?? defaultConfig;
  const Icon = config.icon;

  return (
    <div
      className={cn(
        'flex items-center gap-3 border-b px-4 py-2.5 text-sm',
        config.bg,
        config.border,
        config.text,
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-medium">{announcement.title}</span>
        {announcement.body && <span className="ml-1.5 opacity-80">— {announcement.body}</span>}
      </div>
      <button
        onClick={onDismiss}
        className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100 transition-opacity"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function AnnouncementBanner() {
  const { data: announcements } = useActiveAnnouncements();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  if (!announcements?.length) return null;

  const visible = announcements.filter((a) => !dismissed.has(a.id));
  if (!visible.length) return null;

  return (
    <div>
      {visible.map((announcement) => (
        <SingleBanner
          key={announcement.id}
          announcement={announcement}
          onDismiss={() => setDismissed((prev) => new Set(prev).add(announcement.id))}
        />
      ))}
    </div>
  );
}
